/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import AuthService from 'cio/services/auth';
import SubscriptionCenterService, {
  BulkPreferenceUpdateParams,
  MessageData,
  MessageListData,
} from 'cio/services/subscription-center';
import { SendToUnsubscribedValues, SetPreferencesType } from 'cio/utils/subscription-center';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import { isEmpty } from '@ember/utils';
import { alias } from '@ember/object/computed';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';

interface SubscriptionCenterTopicsSetPreferencesArgs {
  type: SetPreferencesType;
  page: number;
  searchString: string | null;
  searchTagIds: string[];
  searchTopicId: string | null;
  sendToUnsubscribed: SendToUnsubscribedValues | null;
  clearAudienceSearch: () => unknown;
  searchType: string | null;
  searchAudience: string | null;
  updateQuery: (key: string, value: string | number | string[] | null | boolean) => unknown;
  activeMessages: MessageListData;
  pendingMessages: MessageListData;
  completedMessages: MessageListData;
  allMessages: MessageListData;
  availableTypes: SelectOptions[];
  setActiveTab: (type: SetPreferencesType) => unknown;
  filterEnabled: boolean;
  isLoading: boolean;
}

type SelectOptions = {
  value: string | boolean | null;
  label: string;
};

export default class SubscriptionCenterTopicsSetPreferences extends Component<SubscriptionCenterTopicsSetPreferencesArgs> {
  @service auth!: AuthService;
  @service subscriptionCenter!: SubscriptionCenterService;

  readonly Type = SetPreferencesType;
  readonly itemsPerPage = 25;

  @tracked selectedItems: MessageData[] = [];
  @tracked selectedAudience: null | SelectOptions = null;
  @tracked sendToUnsubscribedPreference?: null | SelectOptions = undefined;
  @tracked searchString = '';

  @alias('args.type') activeTab!: SetPreferencesType;

  get subscriptionTopics() {
    if (this.subscriptionCenter.initialized) {
      return this.subscriptionCenter.topics;
    }

    return [];
  }

  get showAudienceSelection() {
    return this.selectedItems.length > 0;
  }

  get nonSelectedItemsOnPage() {
    return (
      this.args.activeMessages.messageList?.filter((message) =>
        this.selectedItems.every((selectedMessage) => message.uniqueID !== selectedMessage.uniqueID),
      ) ?? []
    );
  }

  get allSelected() {
    if (this.args.activeMessages.messageList?.length === 0 || this.selectedItems.length === 0) {
      return false;
    }

    return this.nonSelectedItemsOnPage.length === 0;
  }

  get allMessagesOnPageIndeterminate() {
    let activeMessageCount = this.args.activeMessages.messageList?.length ?? 0;
    let nonSelectedCount = this.nonSelectedItemsOnPage.length;

    return nonSelectedCount > 0 && activeMessageCount > nonSelectedCount;
  }

  get pendingCount() {
    return this.args.pendingMessages.pagination.total;
  }

  get completedCount() {
    return this.args.completedMessages.pagination.total;
  }

  get allCount() {
    return this.args.allMessages.pagination.total;
  }

  get totalItemsCount() {
    let count = 0;
    if (this.activeTab === SetPreferencesType.Pending) {
      count = this.pendingCount;
    }
    if (this.activeTab === SetPreferencesType.Completed) {
      count = this.completedCount;
    }
    if (this.activeTab === SetPreferencesType.All) {
      count = this.allCount;
    }
    return formatNumberAndPluralize([count, 'item']);
  }

  get activePage() {
    return this.args.activeMessages.pagination.page + 1;
  }

  get topicAudienceOptions() {
    return this.subscriptionTopics
      .map((topic) => ({ value: topic.id, label: topic.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  get disableApplyBtn() {
    let { selectedAudience, sendToUnsubscribedPreference } = this;
    if (sendToUnsubscribedPreference !== undefined) {
      return !this.auth.hasPermission('journeys/subscription_center/edit');
    }
    return isEmpty(selectedAudience) || !this.auth.hasPermission('journeys/subscription_center/edit');
  }

  @action
  resetDefaults() {
    this.selectedItems = [];
    this.resetSelectAudience();
  }

  @action
  resetSelectAudience() {
    this.selectedAudience = null;
    // we set undefined here because null actually means something for this field in the backend.
    // therefore undefined here relates to "untouched"
    this.sendToUnsubscribedPreference = undefined;
  }

  @action
  setSearchString(value: string) {
    this.searchString = value;
  }

  @action
  onSearch(event: FlyKeyboardEvent<HTMLInputElement>) {
    event.preventDefault();
    this.args.updateQuery('searchString', this.searchString);
  }

  @action
  filterSearchType(searchType: SelectOptions | null) {
    this.args.updateQuery('searchType', searchType?.value ?? null);
  }

  @action
  filterAudience(audienceOption: SelectOptions | null) {
    if (audienceOption == null) {
      this.args.clearAudienceSearch();
      return;
    }
    let { value } = audienceOption;
    if (
      value === SendToUnsubscribedValues.AllSubscribed ||
      value === SendToUnsubscribedValues.AllSubscribedAndUnsubscribed
    ) {
      this.args.updateQuery('sendToUnsubscribed', value);
      return;
    }
    this.args.updateQuery('searchTopicId', value);
  }

  @action
  selectAllOrNone(checked: boolean) {
    if (checked && this.args.activeMessages.messageList != null) {
      this.selectedItems = [...this.selectedItems, ...this.nonSelectedItemsOnPage];
    } else {
      this.selectedItems = this.selectedItems.filter((selectedMessage) =>
        this.args.activeMessages.messageList?.every((message) => selectedMessage.uniqueID !== message.uniqueID),
      );
      if (this.selectedItems.length === 0) {
        this.resetSelectAudience();
      }
    }
  }

  @action
  toggleMessageSelection(message: MessageData, index: number, checked: boolean) {
    if (checked) {
      this.selectedItems = [...this.selectedItems, message];
    } else {
      this.selectedItems = this.selectedItems.filter(
        (selectedMessage) => selectedMessage.uniqueID !== message.uniqueID,
      );
      if (this.selectedItems.length === 0) {
        this.resetSelectAudience();
      }
    }
  }

  get audienceOptions(): { groupName: string; options: SelectOptions[] }[] {
    return [
      {
        groupName: 'Topics',
        options: this.topicAudienceOptions,
      },
      {
        groupName: 'Global',
        options: [
          {
            value: SendToUnsubscribedValues.AllSubscribed,
            label: 'All subscribed people',
          },
          {
            value: SendToUnsubscribedValues.AllSubscribedAndUnsubscribed,
            label: 'All subscribed and unsubscribed',
          },
        ],
      },
    ];
  }

  @action
  onAudienceChange(audience: SelectOptions) {
    let { value } = audience;
    if (
      value === SendToUnsubscribedValues.AllSubscribed ||
      value === SendToUnsubscribedValues.AllSubscribedAndUnsubscribed
    ) {
      this.sendToUnsubscribedPreference = audience;
      this.selectedAudience = null;
      return;
    }
    this.sendToUnsubscribedPreference = undefined;
    this.selectedAudience = audience;
  }

  @action
  onUpdate() {
    this.resetDefaults();
  }

  @action
  applyPreferences() {
    let newsletterIDs: number[] = [];
    let campaignIDs: number[] = [];

    this.selectedItems.forEach((message) => {
      if (message.type === 'newsletter') {
        newsletterIDs.push(message.id);
      } else {
        campaignIDs.push(message.id);
      }
    });

    let preferenceName = '';
    if (this.sendToUnsubscribedPreference !== undefined) {
      preferenceName = this.sendToUnsubscribedPreference?.label ?? '';
    } else {
      preferenceName = this.selectedAudience?.label ?? '';
    }

    let sendToUnsub: boolean | undefined | null = undefined;
    if (this.sendToUnsubscribedPreference?.value === SendToUnsubscribedValues.AllSubscribed) {
      sendToUnsub = null;
    } else if (this.sendToUnsubscribedPreference?.value === SendToUnsubscribedValues.AllSubscribedAndUnsubscribed) {
      sendToUnsub = true;
    }

    let requestParams: BulkPreferenceUpdateParams = {
      campaignIDs,
      newsletterIDs,
      preferenceName,
    };

    if (sendToUnsub !== undefined) {
      requestParams = { ...requestParams, sendToUnsub };
    } else if (this.selectedAudience?.value !== undefined) {
      requestParams = { ...requestParams, topicID: Number(this.selectedAudience?.value) };
    }

    this.subscriptionCenter.applyPreferences.perform(requestParams);
    this.resetDefaults();
  }
}
